import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import {
	HoverCard,
	HoverCardContent,
	HoverCardTrigger,
} from "@/components/ui/hover-card";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

export interface RowTag {
	key: string;
	label: string;
	hint: string;
	variant: "outline" | "accent";
}

const badgeClass =
	"text-[10px] uppercase !px-2 rounded-full shrink-0 text-nowrap";

// Informational tags in a table row, shared by the Drafts and Orders tables. A tag an admin
// can act on does not belong here: this collapses, and an action must never hide behind a count.
export function RowTags({
	tags,
	className = "",
}: {
	tags: RowTag[];
	className?: string;
}) {
	const { t } = useTranslation("appointments");

	if (!tags.length) {
		return (
			<span className={cn("text-sm text-muted-foreground", className)}>
				{t("drafts.na")}
			</span>
		);
	}

	// Past one tag the row collapses to a count. The tags facet only grows, and a row of
	// four badges sets the column's min-content width for the whole table (see w-max
	// below), so one row with a long tag list would widen the column for every row.
	if (tags.length > 1) {
		return (
			<HoverCard openDelay={100}>
				<HoverCardTrigger asChild>
					<Badge
						variant="outline"
						// Radix opens a HoverCard on focus as well as hover, but only if the
						// trigger can take focus, and a Badge is a span.
						tabIndex={0}
						className={cn(badgeClass, "cursor-default", className)}
					>
						{t("drafts.badges.count", { count: tags.length })}
					</Badge>
				</HoverCardTrigger>
				<HoverCardContent side="top" className="w-72 space-y-2">
					{tags.map((tag) => (
						<div key={tag.key} className="space-y-1">
							<Badge variant={tag.variant} className={badgeClass}>
								{tag.label}
							</Badge>
							<p className="text-xs text-muted-foreground">{tag.hint}</p>
						</div>
					))}
				</HoverCardContent>
			</HoverCard>
		);
	}

	return (
		// w-max is what keeps the badges side by side. The table is `w-full` with auto
		// layout, so the browser squeezes a narrow column down to its min-content width,
		// here the widest single badge, and flex-wrap dutifully stacks the rest. Setting
		// width to max-content makes the row of badges the column's min-content instead, so
		// the column claims the width it needs and the table scrolls if it must. flex-wrap
		// stays for the detail panel, where the parent does constrain the width.
		<div className={cn("flex items-center gap-1 flex-wrap w-max", className)}>
			<TooltipProvider>
				{tags.map((tag) => (
					<Tooltip key={tag.key}>
						<TooltipTrigger asChild>
							<Badge variant={tag.variant} className={badgeClass}>
								{tag.label}
							</Badge>
						</TooltipTrigger>
						<TooltipContent side="top">
							<span>{tag.hint}</span>
						</TooltipContent>
					</Tooltip>
				))}
			</TooltipProvider>
		</div>
	);
}
